import {
  TGenericResponse,
  TResponseServerAction,
  TPayloadVideo,
  TVideo,
  TPayloadVideoComment,
  TVideoComment,
} from "./types";
import { api, ErrorResponseData, type AxiosError } from "@/lib/axios";
import { getCookieByName } from "@/lib/cookies";
import { revalidatePath } from "next/cache";

/** API may return `id` and/or `videoId`; TVideo requires `id` for UI keys. */
function normalizeRandomVideoPayload(body: unknown): TVideo | undefined {
  if (body == null || typeof body !== "object") return undefined;
  const o = body as Record<string, unknown>;
  const idRaw = o.id ?? o.videoId;
  const id = typeof idRaw === "string" ? idRaw : String(idRaw ?? "");
  if (!id) return undefined;
  return { ...(o as unknown as TVideo), id };
}

export const getVideo = async (params: {
  filter: string;
}): Promise<TResponseServerAction<TVideo>> => {
  const xApiKey = await getCookieByName("x-api-key");
  try {
    const start = performance.now();
    const response = await api.get<TGenericResponse<TVideo>>(`/video/random`, {
      params,
      headers: {
        Authorization: xApiKey,
      },
    });
    console.log(
      `[getVideo] /video/random took ${(performance.now() - start).toFixed(0)}ms`,
    );

    const body = response.data?.data;
    const video = normalizeRandomVideoPayload(body);
    if (!video?.id) {
      return {
        status: "error",
        message:
          (response.data?.data as { message?: string } | undefined)?.message ??
          "No video available",
      };
    }

    return {
      status: "success",
      message: "Files fetched successfully",
      metaData: video,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Files fetch failed"
          : "Files fetch failed",
    };
  }
};

export const getVideoFromFollowing = async (params: {
  filter: string;
}): Promise<TResponseServerAction<TVideo>> => {
  const xApiKey = await getCookieByName("x-api-key");
  try {
    const response = await api.get<TGenericResponse<TVideo>>(
      `/video/following`,
      {
        params,
        headers: {
          Authorization: xApiKey,
        },
      },
    );

    const body = response.data?.data;
    const video = normalizeRandomVideoPayload(body);
    if (!video?.id) {
      return {
        status: "error",
        message:
          (response.data?.data as { message?: string } | undefined)?.message ??
          "No video available",
      };
    }

    return {
      status: "success",
      message: "Files fetched successfully",
      metaData: video,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Files fetch failed"
          : "Files fetch failed",
    };
  }
};

export const getVideoV3Url = async (
  videoId: string,
): Promise<TResponseServerAction<string>> => {
  const xApiKey = await getCookieByName("x-api-key");
  try {
    const response = await api.get<
      TGenericResponse<{
        videoId: string;
        isReadyToStream: boolean;
        masterPlaylistUrl: string;
      }>
    >(`/Video/v3/${videoId}`, {
      headers: {
        Authorization: xApiKey,
      },
    });

    return {
      status: "success",
      message: "Video URL fetched successfully",
      metaData: response.data.data.masterPlaylistUrl,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError });
    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Video URL fetch failed"
          : "Video URL fetch failed",
    };
  }
};

export const resolveV3VideoUrl = async (url: string): Promise<string> => {
  if (!url?.includes("/v3/")) return url;
  const v3Id = url.split("/v3/").pop();
  if (!v3Id) return url;
  const res = await getVideoV3Url(v3Id);
  if (res.status === "success" && res.metaData) {
    return res.metaData;
  }
  return url;
};

export const getVideoById = async (
  videoId: string,
): Promise<TResponseServerAction<TVideo>> => {
  const xApiKey = await getCookieByName("x-api-key");
  try {
    const response = await api.get<TGenericResponse<TVideo>>(
      `/video/${videoId}`,
      {
        headers: {
          Authorization: xApiKey,
        },
      },
    );

    console.log({ response });

    // For client components, return success with the data
    return {
      status: "success",
      message: "Files fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError });

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Files fetch failed"
          : "Files fetch failed",
    };
  }
};

export const uploadVideo = async (
  payload: TPayloadVideo,
): Promise<
  TResponseServerAction<{ isSaved: boolean; url: string; id?: string }>
> => {
  try {
    const formData = new FormData();
    formData.append("VideoFile", payload.VideoFile);
    formData.append("Thumbnail", payload.Thumbnail);
    formData.append("Description", payload.Description);
    formData.append("Position", payload.Position || "");
    formData.append("Experience", String(payload.Experience));
    formData.append("CountryShortName", "US");
    formData.append("EmploymentType", payload.EmploymentType || "");
    formData.append("WorkArrangement", payload.WorkArrangement || "");
    formData.append("VideoType", payload.VideoType);
    formData.append("Tags", payload.Tags || "");

    const SubmittedTo = await getCookieByName("SubmittedTo");
    if (SubmittedTo) {
      formData.append("SubmittedTo", SubmittedTo);
    }

    const response = await api.post<TGenericResponse<string | null>>(
      `/Video/upload`,
      formData,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/");

    console.log({ response });

    // For client components, return success with the data
    return {
      status: "success",
      message: "Post has been posted",
      metaData: {
        id: response.data.data || undefined,
        url: `/video/${response.data.data}`,
        isSaved: false,
      },
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError: axiosError.response });
    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Post upload failed"
          : "Post upload failed",
    };
  }
};

export const likeVideoById = async (
  videoId: string,
): Promise<TResponseServerAction<null>> => {
  try {
    const token = await getCookieByName("x-api-key");

    await api.post<TGenericResponse<null>>(
      `/Video/${videoId}/like`,
      {},
      {
        headers: {
          Authorization: token,
        },
      },
    );

    revalidatePath("/");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Liked successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError: axiosError.response });
    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Like failed"
          : "Like failed",
    };
  }
};

export const saveVideoById = async (
  videoId: string,
): Promise<TResponseServerAction<null>> => {
  try {
    const token = await getCookieByName("x-api-key");

    await api.post<TGenericResponse<null>>(
      `/Video/${videoId}/save`,
      {},
      {
        headers: {
          Authorization: token,
        },
      },
    );

    revalidatePath("/");
    revalidatePath("/me/saved");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Saved successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError: axiosError.response });
    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Save failed"
          : "Save failed",
    };
  }
};

export const shareVideoById = async (
  videoId: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.post<TGenericResponse<null>>(
      `/Video/${videoId}/share`,
      {},
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Saved successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError: axiosError.response });
    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Save failed"
          : "Save failed",
    };
  }
};

export const commentVideoById = async (
  videoId: string,
  payload: TPayloadVideoComment,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.post<TGenericResponse<null>>(
      `/Video/${videoId}/comment`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      },
    );

    revalidatePath("/");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Commented successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError: axiosError.response });
    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Comment failed"
          : "Comment failed",
    };
  }
};

export const deleteVideoById = async (
  videoId: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(`/Video/${videoId}`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    revalidatePath("/");

    return {
      status: "success",
      message: "Video deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError: axiosError.response });
    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Delete video failed"
          : "Delete video failed",
    };
  }
};

export const deleteCommentVideoById = async (
  commentId: string,
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete<TGenericResponse<null>>(`/Video/comment/${commentId}`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    revalidatePath("/");

    // For client components, return success with the data
    return {
      status: "success",
      message: "Comment deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError: axiosError.response });
    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Delete comment failed"
          : "Delete comment failed",
    };
  }
};

export const getVideoCommentsByVideoId = async (
  videoId: string,
  params: {
    pageNumber: number;
    pageSize: number;
  },
): Promise<TResponseServerAction<TVideoComment[]>> => {
  const xApiKey = await getCookieByName("x-api-key");
  try {
    const response = await api.get<TGenericResponse<TVideoComment[]>>(
      `/video/${videoId}/comments`,
      {
        params,
        headers: {
          Authorization: xApiKey,
        },
      },
    );

    // For client components, return success with the data
    return {
      status: "success",
      message: "Comments fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;
    console.log({ axiosError });

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Comments fetch failed"
          : "Comments fetch failed",
    };
  }
};

export const getVideoCompanies = async (): Promise<
  TResponseServerAction<string[]>
> => {
  const xApiKey = await getCookieByName("x-api-key");
  try {
    const response = await api.get<TGenericResponse<string[]>>(
      `/video/companies`,
      {
        headers: {
          Authorization: xApiKey,
        },
      },
    );

    return {
      status: "success",
      message: "Companies fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Companies fetch failed"
          : "Companies fetch failed",
    };
  }
};

export const getVideosByCompanyName = async (
  companyName: string,
): Promise<
  TResponseServerAction<
    {
      position: "frontend developer";
      videos: TVideo[];
    }[]
  >
> => {
  const xApiKey = await getCookieByName("x-api-key");
  try {
    const response = await api.get<
      TGenericResponse<
        {
          position: "frontend developer";
          videos: TVideo[];
        }[]
      >
    >(`/video/company/${companyName}/videos`, {
      headers: {
        Authorization: xApiKey,
      },
    });

    console.log({ response: JSON.stringify(response.data.data) });

    return {
      status: "success",
      message: "Company videos fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Company videos fetch failed"
          : "Company videos fetch failed",
    };
  }
};

export const getCompanyVideoById = async (
  videoId: string,
): Promise<TResponseServerAction<TVideo>> => {
  const xApiKey = await getCookieByName("x-api-key");
  try {
    const response = await api.get<TGenericResponse<TVideo>>(
      `/video/company/video/${videoId}`,
      {
        headers: {
          Authorization: xApiKey,
        },
      },
    );

    return {
      status: "success",
      message: "Company video fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Company video fetch failed"
          : "Company video fetch failed",
    };
  }
};
